import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';

const NewRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    description: '',
    request_type: 'access',
    priority: 'normal',
    template_id: '',
    requester_contact_id: '',
    additional_notes: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data || []);
    } catch (error) {
      toast.error('Failed to load clients');
    }
  };

  const handleClientSelect = async (clientId) => {
    setSelectedClient(clientId);
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      template_id: '',
      title: ''
    }));
    setSelectedTemplate(null);
    
    // Fetch templates for this client
    setLoadingTemplates(true);
    try {
      const res = await api.get(`/clients/${clientId}/request-templates`);
      setTemplates(res.data.templates || []);
      
      // Also fetch contacts for this client
      const contactRes = await api.get(`/clients/${clientId}/contacts`);
      setContacts(contactRes.data || []);
    } catch (error) {
      toast.error('Failed to load templates for this client');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setFormData(prev => ({
        ...prev,
        template_id: templateId,
        title: template.name,
        request_type: template.category,
        description: template.description || ''
      }));
      toast.success('Template loaded');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }

    if (!formData.template_id) {
      toast.error('Please select a service package');
      return;
    }

    if (!formData.requester_contact_id) {
      toast.error('Please select who is requesting this');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        client_id: formData.client_id,
        template_id: formData.template_id,
        title: formData.title,
        description: formData.description || `Request for: ${selectedTemplate?.name}`,
        request_type: formData.request_type,
        priority: formData.priority,
        requester_contact_id: formData.requester_contact_id,
        request_data: {
          template_provisions: selectedTemplate?.provisions || {},
          additional_notes: formData.additional_notes
        }
      };

      const res = await api.post('/requests', requestData);
      toast.success('Request submitted successfully');
      navigate(`/requests/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/requests')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Requests
        </Button>
        <h1 className="text-2xl font-bold text-slate-800">New Request</h1>
        <p className="text-sm text-slate-500 mt-1">
          Submit a new user access or service request
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Select Client *</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={formData.client_id}
              onValueChange={handleClientSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Template Selection - Only show after client is selected */}
        {selectedClient && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Select Service Package *</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                  <span className="ml-2 text-sm text-gray-600">Loading templates...</span>
                </div>
              ) : templates.length > 0 ? (
                <>
                  <Select
                    value={formData.template_id}
                    onValueChange={handleTemplateSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a service package..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTemplate && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-2">{selectedTemplate.description}</p>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">This package includes:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedTemplate.provisions?.security_groups?.length > 0 && (
                            <li>Security: {selectedTemplate.provisions.security_groups.join(', ')}</li>
                          )}
                          {selectedTemplate.provisions?.mailbox_access?.length > 0 && (
                            <li>Email: {selectedTemplate.provisions.mailbox_access.join(', ')}</li>
                          )}
                          {selectedTemplate.provisions?.printer_access && (
                            <li>Printer: {selectedTemplate.provisions.printer_access}</li>
                          )}
                          {selectedTemplate.provisions?.software_access?.length > 0 && (
                            <li>Software: {selectedTemplate.provisions.software_access.join(', ')}</li>
                          )}
                          {selectedTemplate.provisions?.network_access?.length > 0 && (
                            <li>Network: {selectedTemplate.provisions.network_access.join(', ')}</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>No service packages available for this client.</p>
                  <p className="text-sm mt-1">Contact your administrator to create templates.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Request Details - Only show after template is selected */}
        {selectedTemplate && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="requester">Who is this request for? *</Label>
                <Select
                  value={formData.requester_contact_id}
                  onValueChange={(value) => handleChange('requester_contact_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a contact..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(contact => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} ({contact.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="priority">Priority *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => handleChange('priority', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="additional_notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="additional_notes"
                  value={formData.additional_notes}
                  onChange={(e) => handleChange('additional_notes', e.target.value)}
                  placeholder="Any special requests or additional information..."
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">
                  e.g., "User starts Monday", "Needs access by end of week", etc.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {selectedTemplate && (
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/requests')}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default NewRequest;
